/*
Construct that has to be inserted by backend.

var TRACKING_PARAMS = {
    "{{tracking endpoint id}}": {
        "stage": "{{stage}}",
        "params": {
            "{{key}}": "{{value}}"
        }
    }
};
*/

/**
 *  Tracking in regards of the permissions requested from TCF API.
 *  TCF API is handled by the PPP Team of MaM.
 *  PPP Board: https://dp-jira.1and1.com/secure/RapidBoard.jspa?rapidView=908&projectKey=PPP
 *  Technical Concept: https://mam-confluence.1and1.com/x/uFBCCQ
 */
class Permission_Tracking {
    constructor() {
        this.tcf = {
            brain: {
                consent: window.consent?.full || window.consent?.features?.brainTracking || false,
                exclude: ['hid', 'resolution_h', 'resolution_w', 'screenresolution', 'var', 'view_h', 'view_w', 'viewport', 'viewport_h', 'viewport_w']
            }
        }

        this.attribute = {
            viewport: {
                width: Math.max(document.documentElement.clientWidth, window.innerWidth || 0),
                height: Math.max(document.documentElement.clientHeight, window.innerHeight || 0)
            },
            screen: {
                width: window.screen.width || 0,
                height: window.screen.height || 0
            },
            random: parseInt(Math.random() * 100000, 10),
            url: location.href,
            timestamp: (new Date().getTime()),
            referrer: document.referrer,
            brand: document.body.dataset?.brand || false,
            lang: document.documentElement.lang,
            title: document.title,
            mediumcode: this._url_query('mc'),
            slug: {
                brand: (document.body.dataset?.brand || false)
            }
        }

        /**
         * Applicable Trackings.
         * Tracking endpoint ids: uimserv, brain
         * Stages: live, prelive, qa, dev
         * default -> live
         *
         * @type object
         */
        this.trackings = {
            uimserv: {
                stage: {
                    live: 'uidbox.uimserv.net/cgi-bin'
                },
                syntax: '//{stage}/{params}&{frontend}',
                params: ['no use for the moment'],
                frontend: {
                    d: '{random}',
                    tc: '{timestamp}',
                    r: '{referrer}',
                    site: '{brand}',
                    region: '{lang}'
                }
            },
            brain: {
                stage: {
                    live: 'wa',
                    qa: 'wa-qa'
                },
                syntax: '//{stage}.{brand}/{tmp_bi_brand}/{tmp_bi_brand}/s?brand={tmp_bi_brand}&portal={tmp_bi_brand}&{frontend}&{params}',
                params: ['no use for the moment'],
                frontend: {
                    viewport: (this.tcf.brain.consent) ? '{viewport.width}x{viewport.height}' : false,
                    screenresolution: (this.tcf.brain.consent) ? '{screen.width}x{screen.height}' : false,
                    ts: '{timestamp}',
                    country: '{lang}',
                    title: '{title}',
                    pageurl: '{url}',
                    referrer: '{referrer}',
                    mc: (this.tcf.brain.consent) ? '{mediumcode}' : false

                }
            }
        }
        this.fire_custom_track_buttons()
    }


    /**
     * Initializes the tracking.
     */
    init() {
        window.TRACKING_PARAMS = window.TRACKING_PARAMS || {}
        if (typeof window.TRACKING_PARAMS == 'object') {
            Object.entries(window.TRACKING_PARAMS).forEach(([tracking_type, value]) => {
                this.firePixel(tracking_type, value)
            })
        }
    }


    /**
     * Fires events on click for custom buttons on legacy pages.
     * Buttons need at least data-tracking-type and data-tracking-paramstring attributes to be recognized.
     */
    fire_custom_track_buttons() {
        const custom_track_buttons = document.querySelectorAll('[data-tracking-type][data-tracking-paramstring]')
        Array.from(custom_track_buttons).forEach(btn => {
            const endpointId = btn.dataset.trackingType
            const paramString = btn.dataset.trackingParamstring
            const stage = btn.dataset.trackingStage

            const clickEvent = event => {
                // Button 1 is primary button click on mouse event
                // for people that switched the functionality (e.g. left handed) it will be the right mouse button (which is correct)
                const firePixel = event.type === 'touchstart' || (event.type === 'mousedown' && !!event.buttons && event.buttons === 1)
                if (!firePixel) return true

                event.preventDefault()
                this.firePixel(endpointId, {paramString, stage})
            }

            btn.addEventListener('mousedown', clickEvent)
            btn.addEventListener('touchstart', clickEvent)
            document.addEventListener('pixelFired', event => {
                const {identifier: ev_endpointId, stage: ev_stage, paramString: ev_paramString} = event.detail.info
                if (!(ev_endpointId === endpointId && ev_stage === stage && ev_paramString === paramString)) return

                btn.click()
            })
        })
    }

    /**
     * Fires a Tracking pixel based on the given values
     * @param  {string} endpointId Tracking endpoint id for specific system (e.g. brain)
     * @param  {object} trackingParams Given params for the Tracking endpoint
     */
    firePixel(endpointId, trackingParams) {
        // Tracking was not identified; no sense to continue
        if (!this.trackings?.[endpointId]) {
            return
        }

        const endpointStage = this.trackings[endpointId].stage
        const stage = trackingParams.stage in endpointStage ? endpointStage[trackingParams.stage] : endpointStage.live
        let url_string = this.trackings[endpointId].syntax.replace('{stage}', stage)

        // The params given by the backend
        if (!trackingParams.paramString) {
            let tmp_backend_params = []
            if (trackingParams?.params) {
                for (const key of Object.keys(trackingParams.params)) {
                    let add = !this.tcf?.[endpointId] // add if tracking type is not specified
                    add = add || this.tcf?.[endpointId]?.consent // add if consent is given
                    add = add || this.tcf?.[endpointId] && !this.tcf?.[endpointId]?.exclude.includes(key) // add if consent is given
                    add = add && trackingParams.params[key] !== 'undefined' && trackingParams.params[key] !== 'undef' // add if not undefined
                    if (add) {
                        tmp_backend_params.unshift(`${encodeURIComponent(key)}=${encodeURIComponent(trackingParams.params[key])}`)
                    }
                }
            }
            url_string = url_string.replace('{params}', tmp_backend_params.join('&'))
        } else {
            url_string = url_string.replace('{params}', trackingParams.paramString)
        }

        // Technical debt for BI
        // Bi backend is currently not able to accept standardized slugified brand as value
        if (url_string.includes('tmp_bi_brand')) {
            let bi_brand = this.attribute.brand
            if (!bi_brand) {
                // kill .server.lan for local testing
                const location_parts = location.host.replace('.server.lan', '').split('.')
                const brand = location_parts[location_parts.length - 2]
                const tld = location_parts[location_parts.length - 1]
                bi_brand = `${brand}.${tld}`
                this.attribute.brand = bi_brand
            }

            const removeDot = ['web.de', 'mail.com']
            bi_brand = removeDot.includes(bi_brand) ? bi_brand.replace('.', '') : bi_brand.split('.')[0]

            url_string = url_string.replace(/{tmp_bi_brand}/g, bi_brand)
        }


        // Got any Frontend Params?
        if (this.trackings[endpointId]?.frontend) {
            let frontend_params = this.trackings[endpointId].frontend

            for (let item in frontend_params) {
                if (!frontend_params[item]) {
                    continue
                }

                frontend_params[item] = frontend_params[item].replace(/\{(.*?)\}/g, (entries) => {
                    entries = entries.replace('{', '').replace('}', '')

                    if (entries.includes('.')) {
                        const tmp = entries.split('.')
                        if (this.attribute?.[tmp[0]]) {
                            return this.attribute[tmp[0]][tmp[1]]
                        }
                    } else {
                        return this.attribute[entries]
                    }
                })
            }

            let tmp_frontend_params = []
            for (const key of Object.keys(frontend_params)) {
                let add = !this.tcf?.[endpointId] // add if tracking type is not specified
                add = add || this.tcf?.[endpointId]?.consent // add if consent is given
                add = add || this.tcf?.[endpointId] && !this.tcf?.[endpointId]?.exclude.includes(key) // add if consent is given
                add = add && !(key in TRACKING_PARAMS[endpointId].params)// add if not already added via backend
                if (add) {
                    tmp_frontend_params.unshift(`${encodeURIComponent(key)}=${encodeURIComponent(frontend_params[key])}`)
                }
            }

            url_string = url_string.replace('{frontend}', tmp_frontend_params.join('&'))

        }

        // got anything left?
        // In case a frontend param was specific put in the pixel url string
        if (url_string.includes('{')) {
            url_string = url_string.replace(/\{(.*?)\}/g, ((entries) => {
                entries = entries.replace('{', '').replace('}', '')

                if (entries.includes('.')) {
                    const tmp = entries.split('.')
                    if (this.attribute?.[tmp[0]]) {
                        return this.attribute[tmp[0]][tmp[1]]
                    }
                } else {
                    return this.attribute?.[entries] || ''
                }
            }).bind(this))
        }

        // Let's create the Pixel
        this.fire_pixel(url_string, endpointId, trackingParams)

    }

    fire_pixel(url, id, {stage, params, paramString}) {
        let timeout = false
        const image = new Image(1, 1)
        image.src = url

        const fire_pixel_event = ({success, timeout}) => {
            const pixelFired = new CustomEvent('pixelFired', {
                detail: {
                    bubbles: true,
                    success,
                    timeout,
                    info: {
                        identifier: id,
                        stage,
                        params,
                        paramString
                    }
                }
            })
            document.dispatchEvent(pixelFired)
        }

        const timer = setTimeout(() => {
            timeout = true
            fire_pixel_event({success: false, timeout: true})
        }, 6000)

        image.onload = () => {
            if (!timeout) {
                clearTimeout(timer)
                fire_pixel_event({success: true, timeout: false})
            }
        }

        image.onerror = () => {
            if (!timeout) {
                clearTimeout(timer)
                fire_pixel_event({success: false, timeout: false})
            }
        }
    }


    /**
     * Trigger Tracking
     * @param  {string} endpointId The Tracking identifier
     * @param  {object} params     Trackparameter, usually provided by Backend
     * @param  {'qa'|'live'} stage Where the pixel will be fired
     */
    trigger(endpointId, params, stage = 'live') {
        this.firePixel(endpointId, {stage, params})
    }


    /**
     * Return the required url parameter
     * @param  {string} param the required URL parameter
     * @return {string}       the value of the req param or undefined
     */
    _url_query(param) {
        let vars = {}
        ;('' + document.URL).replace(/[?&]+([^=&]+)=([^&]*)/gi, (m, key, value) => {
            vars[decodeURIComponent(key)] = decodeURIComponent(value)
        })

        return vars[param]
    }

    /**
     * CURRENTLY UNUSED AND THEREFORE TEMPORARILY COMMENTED
     *
     * slugify given string
     * @param  {string} str string to be slugified
     * @return {string} slugified string
    _slugify(str) {
        return str.toString().toLowerCase()
            .replace(/\s+/g, '-')
            .replace(/[^\w\-]+/g, '')
    }
     */
}

window.PTRACKING = new Permission_Tracking()

if (typeof window.TRACKING_PARAMS !== 'undefined') {
    window.PTRACKING.init()
} else {
    document.addEventListener('DOMContentLoaded', function () {
        window.PTRACKING.init()
    })
}
